import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calculator,
  Users,
  CheckCircle,
  MessageCircle,
  ChevronDown,
} from 'lucide-react';
import BookingCalendar from './BookingCalendar';
import {
  soundHirePackages,
  soundAddOns,
  smokeMachinePackages,
  venueSupportPackages,
} from '../data/ubuntuServices';
import {
  loadEstimateSelections,
  patchEstimateSelections,
  setEstimateEventContext,
} from '../utils/estimateSelections';

const EstimateCalculator = () => {
  const [activeTab, setActiveTab] = useState('calculator');
  const [activeBuilderStep, setActiveBuilderStep] = useState(1);
  const [mobileSectionsOpen, setMobileSectionsOpen] = useState({
    basics: true,
    addOns: false,
  });
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    venueLocation: '',
    guests: 50,
    mealType: 'Option 1',
    saladOptions: { count: 0, type: 'Mixed Green Salad' },
    veggieOptions: { count: 0, type: 'Grilled Vegetables' },
    dessertOptions: { count: 0, type: 'Chocolate Cake' },
    extraMeatItems: {
      'Chicken Portions': 0,
      'Lamb Chops': 0,
      'Boerewors': 0,
      'Beef Kebabs': 0,
      'Chicken Kebabs': 0,
      'Lamb Kebabs': 0,
      'Potjie Meal (1)': 0,
      'Potjie Meal (2)': 0
    },
    extraItems: {
      'Bread Rolls': 0,
      'Garlic Loaves': 0,
      'French Loaves': 0,
      'Rice': 0,
      'Pap': 0,
      'Samp': 0
    },
    staffOptions: { 'Barmen': 0, 'Waiters': 0 },
    beverages: {
      'Soft Drinks': 0,
      'Juices': 0,
      'Water (500ml)': 0,
      'Coffee': 0,
      'Tea': 0,
      'Hot Chocolate': 0,
      'Energy Drinks': 0,
      'Sparkling Water': 0
    },
    decorPackage: 'Basic',
    spitBraaiRental: false,
    teamBuildingAddons: [],

    // Sound / smoke / venue selections (flat service/event fees)
    soundHirePackageId: 'none',
    soundAddOnIds: [],
    smokeMachinePackageId: 'none',
    venueSupportPackageId: 'none',
    venuePreferences: {
      preferredLocation: '',
      indoorOutdoor: '',
      guestCount: '',
      preferredEventDate: '',
      maxVenueBudget: '',
    },
    hireItems: {
      tents: 0,
      chairs: 0,
      tables: 0,
    },
  });

  const [totalEstimate, setTotalEstimate] = useState(0);
  const [selectionsLoaded, setSelectionsLoaded] = useState(false);
  const [individualMode, setIndividualMode] = useState({
    extraMeatItems: false,
    extraItems: false,
    staffOptions: false,
    beverages: false
  });

  const builderSteps = [
    { id: 1, label: 'Catering' },
    { id: 2, label: 'Decor' },
    { id: 3, label: 'Spit Braai' },
    { id: 4, label: 'Sound' },
    { id: 5, label: 'Venue' },
    { id: 6, label: 'Extras' },
  ];

  // Pricing logic - memoized to prevent useEffect dependency issues
  const pricing = React.useMemo(() => ({
    mealTypes: {
      'Option 1': 190,
      'Option 2': 280,
      'Option 3': 320,
      'Upsized Buffet': 360,
      'Spit Braai': 205,
      'Finger Food': 180,
      'Custom Menu': 400
    },
    salads: {
      'Mixed Green Salad': 65,
      'Caesar Salad': 40,
      'Greek Salad': 60,
      'Coleslaw': 30,
      'Potato Salad': 65
    },
    veggies: {
      'Grilled Vegetables': 40,
      'Roasted Root Vegetables': 40,
      'Stir-fried Vegetables': 45,
      'Vegetable Kebabs': 22
    },
    desserts: {
      'Chocolate Cake': 45,
      'Cheesecake': 40,
      'Fruit Platter': 450,
      'Ice Cream': 45,
      'Tiramisu': 50
    },
    extraMeatItems: {
      'Chicken Portions': 35,
      'Lamb Chops': 85,
      'Boerewors': 55,
      'Beef Kebabs': 55,
      'Chicken Kebabs': 40,
      'Lamb Kebabs': 55,
      'Potjie Meal (1)': 550,
      'Potjie Meal (2)': 750
    },
    extraItems: {
      'Bread Rolls': 45,
      'Garlic Loaves': 60,
      'French Loaves': 35,
      'Rice': 20,
      'Pap': 15,
      'Samp': 35
    },
    staffOptions: {
      'Barmen': 800,
      'Waiters': 550
    },
    decor: {
      'Basic': 8500,
      'Elegant': 1200,
      'Premium': 2500
    },
    spitBraai: 1500, // Standard Hire - matches SpitBraaiPage.js
    teamBuilding: {
      'Quad Biking': 300,
      'Obstacle Course': 200,
      'Outdoor Games': 150
    },
    beverages: {
      'Soft Drinks': 16,
      'Juices': 20,
      'Water (500ml)': 12,
      'Coffee': 20,
      'Tea': 15,
      'Hot Chocolate': 18,
      'Energy Drinks': 25,
      'Sparkling Water': 15
      },

      // Sound Hire (flat)
      soundHirePackages: Object.fromEntries(
        soundHirePackages.map((p) => [p.id, p.price])
      ),
      soundAddOns: Object.fromEntries(
        soundAddOns.map((a) => [a.id, a.price])
      ),
      smokeMachinePackages: Object.fromEntries(
        smokeMachinePackages.map((p) => [p.id, p.price])
      ),
      venueSupportPackages: Object.fromEntries(
        venueSupportPackages.map((p) => [p.id, p.price])
      ),
  }), []);

  // Load quote builder selections added from Sound Hire / Venue Support pages
  useEffect(() => {
    const saved = loadEstimateSelections();
    setFormData((prev) => ({
      ...prev,
      soundHirePackageId: saved.soundHirePackageId || 'none',
      soundAddOnIds: Array.isArray(saved.soundAddOnIds)
        ? saved.soundAddOnIds
        : [],
      smokeMachinePackageId: saved.smokeMachinePackageId || 'none',
      venueSupportPackageId: saved.venueSupportPackageId || 'none',
    }));
    setSelectionsLoaded(true);
  }, []);

  // Persist selections so "Estimate & Book" stays in sync
  useEffect(() => {
    if (!selectionsLoaded) return;
    patchEstimateSelections({
      soundHirePackageId: formData.soundHirePackageId,
      soundAddOnIds: formData.soundAddOnIds,
      smokeMachinePackageId: formData.smokeMachinePackageId,
      venueSupportPackageId: formData.venueSupportPackageId,
      estimatedTotal: totalEstimate,
    });
    setEstimateEventContext({
      name: formData.eventName || '',
      eventDate: formData.eventDate || '',
      guests: formData.guests ? String(formData.guests) : '',
      location: formData.venueLocation || '',
    });
  }, [
    selectionsLoaded,
    formData.soundHirePackageId,
    formData.soundAddOnIds,
    formData.smokeMachinePackageId,
    formData.venueSupportPackageId,
    formData.eventName,
    formData.eventDate,
    formData.guests,
    formData.venueLocation,
    totalEstimate,
  ]);

  // Calculate total estimate
  useEffect(() => {
    let total = 0;

    // Meal type cost
    const mealCost = pricing.mealTypes[formData.mealType] * formData.guests;
    total += mealCost;

    // Salad options
    total += pricing.salads[formData.saladOptions.type] * formData.saladOptions.count;

    // Veggie options
    total += pricing.veggies[formData.veggieOptions.type] * formData.veggieOptions.count;

    // Dessert options
    total += pricing.desserts[formData.dessertOptions.type] * formData.dessertOptions.count;

    // Extra meat items
    Object.entries(formData.extraMeatItems).forEach(([item, count]) => {
      if (count > 0) {
        total += pricing.extraMeatItems[item] * count;
      }
    });

    // Extra items
    Object.entries(formData.extraItems).forEach(([item, count]) => {
      if (count > 0) {
        total += pricing.extraItems[item] * count;
      }
    });

    // Staff options
    Object.entries(formData.staffOptions).forEach(([staff, count]) => {
      if (count > 0) {
        total += pricing.staffOptions[staff] * count;
      }
    });

    // Beverages
    Object.entries(formData.beverages).forEach(([beverage, count]) => {
      if (count > 0) {
        total += pricing.beverages[beverage] * count;
      }
    });

    // Decor cost
    total += pricing.decor[formData.decorPackage];

    // Spit Braai rental
    if (formData.spitBraaiRental) {
      total += pricing.spitBraai;
    }

    // Team building add-ons
    formData.teamBuildingAddons.forEach(activity => {
      total += pricing.teamBuilding[activity] * formData.guests;
    });

    // Sound hire packages + add-ons (flat)
    if (
      formData.soundHirePackageId &&
      formData.soundHirePackageId !== 'none'
    ) {
      total += pricing.soundHirePackages[formData.soundHirePackageId] || 0;
    }

    formData.soundAddOnIds.forEach((addOnId) => {
      total += pricing.soundAddOns[addOnId] || 0;
    });

    // Smoke machine hire (flat)
    if (
      formData.smokeMachinePackageId &&
      formData.smokeMachinePackageId !== 'none'
    ) {
      total +=
        pricing.smokeMachinePackages[formData.smokeMachinePackageId] || 0;
    }

    // Venue support (flat)
    if (
      formData.venueSupportPackageId &&
      formData.venueSupportPackageId !== 'none'
    ) {
      total +=
        pricing.venueSupportPackages[formData.venueSupportPackageId] || 0;
    }

    setTotalEstimate(Math.round(total));
  }, [formData, pricing]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (field, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subField]: value
      }
    }));
  };

  const handleArrayToggle = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const toggleIndividualMode = (section) => {
    setIndividualMode(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleWhatsAppSubmit = () => {
    const soundHirePkg =
      soundHirePackages.find((p) => p.id === formData.soundHirePackageId) ||
      null;
    const smokePkg =
      smokeMachinePackages.find(
        (p) => p.id === formData.smokeMachinePackageId
      ) || null;
    const venuePkg =
      venueSupportPackages.find(
        (p) => p.id === formData.venueSupportPackageId
      ) || null;

    const selectedSoundAddOnNames = formData.soundAddOnIds
      .map((id) => soundAddOns.find((a) => a.id === id)?.name)
      .filter(Boolean);

    const selectedTeamBuilding = formData.teamBuildingAddons.length
      ? formData.teamBuildingAddons.join(', ')
      : 'None selected';
    const hasHireItems =
      formData.hireItems.tents > 0 ||
      formData.hireItems.chairs > 0 ||
      formData.hireItems.tables > 0;

    const whatsappMessage = `
Hi UBUNTU EVENTS MANAGEMENT,
I'd like a quote for the following:

Event details:
*Name:* ${formData.eventName || 'Not provided'}
*Date:* ${formData.eventDate || 'Not provided'}
*Guests:* ${formData.guests}
*Venue/location:* ${formData.venueLocation || 'Not provided'}

Services selected:
*Catering:* ${formData.mealType}
*Decor:* ${formData.decorPackage}
*Spit Braai:* ${formData.spitBraaiRental ? 'Included' : 'None'}
*Team Building:* ${selectedTeamBuilding}
*Sound Hire:* ${
      soundHirePkg ? soundHirePkg.name : 'None'
    }
*Sound Add-ons:* ${
      selectedSoundAddOnNames.length ? selectedSoundAddOnNames.join(', ') : 'None'
    }
*Smoke Machine:* ${smokePkg ? smokePkg.name : 'None'}
*Venue Support:* ${venuePkg ? venuePkg.name : 'None'}
*Tent, Chair & Table Hire:* ${
      hasHireItems
        ? `Tents (${formData.hireItems.tents || 0}), Chairs (${formData.hireItems.chairs || 0}), Tables (${formData.hireItems.tables || 0})`
        : 'None selected'
    }

Estimated Total: R${totalEstimate.toLocaleString()}

Please confirm availability and final pricing.
Thank you!
    `.trim();

    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleHireQuoteRequest = () => {
    const message = `
Hi UBUNTU EVENTS MANAGEMENT, I would like a personalized quotation for the following hire items:
- Chairs: ${formData.hireItems.chairs || 0}
- Tables: ${formData.hireItems.tables || 0}
- Tents: ${formData.hireItems.tents || 0}

Event details:
*Name:* ${formData.eventName || 'Not provided'}
*Date:* ${formData.eventDate || 'Not provided'}
*Guests:* ${formData.guests || 'Not provided'}
*Location:* ${formData.venueLocation || 'Not provided'}

Please assist with availability and a quotation for my event.
    `.trim();

    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const completedStepCount = [
    formData.mealType && formData.guests > 0,
    formData.decorPackage,
    formData.spitBraaiRental,
    formData.soundHirePackageId !== 'none' || formData.soundAddOnIds.length > 0 || formData.smokeMachinePackageId !== 'none',
    formData.venueSupportPackageId !== 'none',
    formData.teamBuildingAddons.length > 0 || formData.hireItems.tents > 0 || formData.hireItems.chairs > 0 || formData.hireItems.tables > 0 || totalEstimate > 0,
  ].filter(Boolean).length;

  // Removed unused handleEmailSubmit function

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="bg-gold/10 p-4 rounded-full">
              <Calculator className="w-12 h-12 text-gold" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
            Build Your Event in 2 Minutes
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Premium estimator for event services Gauteng. Reliable service, experienced team, and full-service event solutions across Pretoria, Johannesburg, Centurion, Midrand, and Sandton.
          </p>
          <p className="text-sm text-gold font-semibold mb-6">Limited availability - weekend bookings fill fast.</p>

          <div className="max-w-3xl mx-auto mb-6">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <span>Progress</span>
              <span>{completedStepCount}/6 steps</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gold transition-all duration-300" style={{ width: `${(completedStepCount / 6) * 100}%` }} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              {builderSteps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveBuilderStep(step.id)}
                  className={`px-3 py-1 rounded-full text-xs ${activeBuilderStep === step.id ? 'bg-gold text-white' : 'bg-white border border-gray-300 text-gray-700'}`}
                >
                  {step.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-center sm:gap-4">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-3 sm:px-6 py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'calculator'
                  ? 'bg-gold text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              💰 Price Calculator
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-3 sm:px-6 py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === 'calendar'
                  ? 'bg-gold text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              📅 Book Date
            </button>
          </div>
        </motion.div>

        {activeTab === 'calendar' ? (
          <BookingCalendar />
        ) : (
          <>

        <div className="grid lg:grid-cols-2 gap-8 pb-28 lg:pb-0">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4"
          >
            {/* Mobile accordion: Event Basics */}
            <div className="lg:hidden">
              <button
                type="button"
                onClick={() =>
                  setMobileSectionsOpen((p) => ({ ...p, basics: !p.basics }))
                }
                className="w-full flex items-center justify-between gap-4 bg-white/80 backdrop-blur border border-gray-200 rounded-2xl px-4 py-3"
                aria-expanded={mobileSectionsOpen.basics}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 text-gold flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="font-semibold text-gray-800">Event Basics</div>
                    <div className="text-sm text-gray-600 truncate">
                      Guests, date, and venue details
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gold transition-transform ${
                    mobileSectionsOpen.basics ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            <div
              className={`${
                mobileSectionsOpen.basics ? 'block' : 'hidden'
              } lg:block space-y-6`}
            >
            {/* Number of Guests */}
            <div className="card">
              <div className="flex items-center mb-4">
                <Users className="w-5 h-5 text-gold mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">Number of Guests</h3>
              </div>
              <div className="space-y-4">
                <input
                  type="range"
                  min="10"
                  max="500"
                  value={formData.guests}
                  onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-sm text-gray-600">
                  <span>10</span>
                  <span className="font-semibold text-gold text-lg">{formData.guests}</span>
                  <span>500</span>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Or type exact number:</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={formData.guests}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value >= 10 && value <= 500) {
                        handleInputChange('guests', value);
                      }
                    }}
                    className="w-24 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center font-semibold"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">📅</div>
                <h3 className="text-lg font-semibold text-gray-800">Event Details</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Name
                  </label>
                  <input
                    type="text"
                    value={formData.eventName}
                    onChange={(e) =>
                      handleInputChange('eventName', e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    placeholder="e.g. John & Jane Wedding"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    value={formData.eventDate}
                    onChange={(e) =>
                      handleInputChange('eventDate', e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue / Location
                </label>
                <input
                  type="text"
                  value={formData.venueLocation}
                  onChange={(e) =>
                    handleInputChange('venueLocation', e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  placeholder="e.g. Pretoria West"
                />
              </div>
            </div>

            {/* Meal Type */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🍖</div>
                <h3 className="text-lg font-semibold text-gray-800">Type of Meal</h3>
              </div>
              <select
                value={formData.mealType}
                onChange={(e) => handleInputChange('mealType', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              >
                {Object.entries(pricing.mealTypes).map(([type, price]) => (
                  <option key={type} value={type}>
                    {type} - R{price}/guest
                  </option>
                ))}
              </select>
            </div>

            {/* Salad Options */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🥗</div>
                <h3 className="text-lg font-semibold text-gray-800">Salad Options</h3>
              </div>
              <div className="space-y-3">
                <select
                  value={formData.saladOptions.type}
                  onChange={(e) => handleNestedInputChange('saladOptions', 'type', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                >
                  {Object.entries(pricing.salads).map(([type, price]) => (
                    <option key={type} value={type}>
                      {type} - R{price}/portion
                    </option>
                  ))}
                </select>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Quantity:</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.saladOptions.count}
                    onChange={(e) => handleNestedInputChange('saladOptions', 'count', parseInt(e.target.value))}
                    className="w-20 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center"
                  />
                </div>
              </div>
            </div>

            {/* Veggie Options */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🥕</div>
                <h3 className="text-lg font-semibold text-gray-800">Veggie Options</h3>
              </div>
              <div className="space-y-3">
                <select
                  value={formData.veggieOptions.type}
                  onChange={(e) => handleNestedInputChange('veggieOptions', 'type', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                >
                  {Object.entries(pricing.veggies).map(([type, price]) => (
                    <option key={type} value={type}>
                      {type} - R{price}/portion
                    </option>
                  ))}
                </select>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Quantity:</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.veggieOptions.count}
                    onChange={(e) => handleNestedInputChange('veggieOptions', 'count', parseInt(e.target.value))}
                    className="w-20 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center"
                  />
                </div>
              </div>
            </div>

            {/* Dessert Options */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🍰</div>
                <h3 className="text-lg font-semibold text-gray-800">Dessert Options</h3>
              </div>
              <div className="space-y-3">
                <select
                  value={formData.dessertOptions.type}
                  onChange={(e) => handleNestedInputChange('dessertOptions', 'type', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                >
                  {Object.entries(pricing.desserts).map(([type, price]) => (
                    <option key={type} value={type}>
                      {type} - R{price}/portion
                    </option>
                  ))}
                </select>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Quantity:</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.dessertOptions.count}
                    onChange={(e) => handleNestedInputChange('dessertOptions', 'count', parseInt(e.target.value))}
                    className="w-20 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center"
                  />
                </div>
              </div>
            </div>

            </div>

            {/* Mobile accordion: Add-ons & Services */}
            <div className="lg:hidden pt-2">
              <button
                type="button"
                onClick={() =>
                  setMobileSectionsOpen((p) => ({ ...p, addOns: !p.addOns }))
                }
                className="w-full flex items-center justify-between gap-4 bg-white/80 backdrop-blur border border-gray-200 rounded-2xl px-4 py-3"
                aria-expanded={mobileSectionsOpen.addOns}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-olive/10 text-olive flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="font-semibold text-gray-800">
                      Add-ons & Services
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      Decor, spit braai, sound, smoke, venue support
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gold transition-transform ${
                    mobileSectionsOpen.addOns ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            <div
              className={`${
                mobileSectionsOpen.addOns ? 'block' : 'hidden'
              } lg:block space-y-6`}
            >
            {/* Extra Meat Items */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 text-gold mr-3 text-lg">🍖</div>
                  <h3 className="text-lg font-semibold text-gray-800">Extra Meat Items</h3>
                </div>
                <button
                  type="button"
                  onClick={() => toggleIndividualMode('extraMeatItems')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                    individualMode.extraMeatItems
                      ? 'bg-gold text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Individual
                </button>
              </div>
              
              {individualMode.extraMeatItems ? (
                <div className="space-y-4">
                  {Object.entries(pricing.extraMeatItems).map(([item, price]) => (
                    <div key={item} className="p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">
                          {item} - R{price} each
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-600">Quantity:</label>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.extraMeatItems[item] || 0;
                              if (currentCount > 0) {
                                handleNestedInputChange('extraMeatItems', item, currentCount - 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={formData.extraMeatItems[item] || 0}
                            onChange={(e) => {
                              const value = Math.max(0, Math.min(50, parseInt(e.target.value) || 0));
                              handleNestedInputChange('extraMeatItems', item, value);
                            }}
                            className="w-16 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.extraMeatItems[item] || 0;
                              if (currentCount < 50) {
                                handleNestedInputChange('extraMeatItems', item, currentCount + 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">
                          Total: R{((formData.extraMeatItems[item] || 0) * price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(pricing.extraMeatItems).map(([item, price]) => (
                    <label key={item} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(formData.extraMeatItems[item] || 0) > 0}
                        onChange={() => {
                          const currentCount = formData.extraMeatItems[item] || 0;
                          handleNestedInputChange('extraMeatItems', item, currentCount > 0 ? 0 : 1);
                        }}
                        className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {item} - R{price} each
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Extra Items */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 text-gold mr-3 text-lg">🍞</div>
                  <h3 className="text-lg font-semibold text-gray-800">Extra Items</h3>
                </div>
                <button
                  type="button"
                  onClick={() => toggleIndividualMode('extraItems')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                    individualMode.extraItems
                      ? 'bg-gold text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Individual
                </button>
              </div>
              
              {individualMode.extraItems ? (
                <div className="space-y-4">
                  {Object.entries(pricing.extraItems).map(([item, price]) => (
                    <div key={item} className="p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">
                          {item} - R{price} each
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-600">Quantity:</label>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.extraItems[item] || 0;
                              if (currentCount > 0) {
                                handleNestedInputChange('extraItems', item, currentCount - 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.extraItems[item] || 0}
                            onChange={(e) => {
                              const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                              handleNestedInputChange('extraItems', item, value);
                            }}
                            className="w-16 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.extraItems[item] || 0;
                              if (currentCount < 100) {
                                handleNestedInputChange('extraItems', item, currentCount + 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">
                          Total: R{((formData.extraItems[item] || 0) * price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(pricing.extraItems).map(([item, price]) => (
                    <label key={item} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(formData.extraItems[item] || 0) > 0}
                        onChange={() => {
                          const currentCount = formData.extraItems[item] || 0;
                          handleNestedInputChange('extraItems', item, currentCount > 0 ? 0 : 1);
                        }}
                        className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {item} - R{price} each
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Staff Options */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 text-gold mr-3 text-lg">🧑‍🍳</div>
                  <h3 className="text-lg font-semibold text-gray-800">Staff Options</h3>
                </div>
                <button
                  type="button"
                  onClick={() => toggleIndividualMode('staffOptions')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                    individualMode.staffOptions
                      ? 'bg-gold text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Individual
                </button>
              </div>
              
              {individualMode.staffOptions ? (
                <div className="space-y-4">
                  {Object.entries(pricing.staffOptions).map(([staff, price]) => (
                    <div key={staff} className="p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">
                          {staff} - R{price} each
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-600">Quantity:</label>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.staffOptions[staff] || 0;
                              if (currentCount > 0) {
                                handleNestedInputChange('staffOptions', staff, currentCount - 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="20"
                            value={formData.staffOptions[staff] || 0}
                            onChange={(e) => {
                              const value = Math.max(0, Math.min(20, parseInt(e.target.value) || 0));
                              handleNestedInputChange('staffOptions', staff, value);
                            }}
                            className="w-16 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.staffOptions[staff] || 0;
                              if (currentCount < 20) {
                                handleNestedInputChange('staffOptions', staff, currentCount + 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">
                          Total: R{((formData.staffOptions[staff] || 0) * price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(pricing.staffOptions).map(([staff, price]) => (
                    <label key={staff} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(formData.staffOptions[staff] || 0) > 0}
                        onChange={() => {
                          const currentCount = formData.staffOptions[staff] || 0;
                          handleNestedInputChange('staffOptions', staff, currentCount > 0 ? 0 : 1);
                        }}
                        className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-gray-700">
                        {staff} - R{price} each
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Beverages */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 text-gold mr-3 text-lg">🥤</div>
                  <h3 className="text-lg font-semibold text-gray-800">Beverages</h3>
                </div>
                <button
                  type="button"
                  onClick={() => toggleIndividualMode('beverages')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${
                    individualMode.beverages
                      ? 'bg-gold text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Individual
                </button>
              </div>
              
              {individualMode.beverages ? (
                <div className="space-y-4">
                  {Object.entries(pricing.beverages).map(([beverage, price]) => (
                    <div key={beverage} className="p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">
                          {beverage} - R{price} each
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-600">Quantity:</label>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.beverages[beverage] || 0;
                              if (currentCount > 0) {
                                handleNestedInputChange('beverages', beverage, currentCount - 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="200"
                            value={formData.beverages[beverage] || 0}
                            onChange={(e) => {
                              const value = Math.max(0, Math.min(200, parseInt(e.target.value) || 0));
                              handleNestedInputChange('beverages', beverage, value);
                            }}
                            className="w-16 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-center font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentCount = formData.beverages[beverage] || 0;
                              if (currentCount < 200) {
                                handleNestedInputChange('beverages', beverage, currentCount + 1);
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-bold"
                          >
                            +
                          </button>
                        </div>
                        <span className="text-sm text-gray-500">
                          Total: R{((formData.beverages[beverage] || 0) * price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(pricing.beverages).map(([beverage, price]) => (
                    <label key={beverage} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={(formData.beverages[beverage] || 0) > 0}
                        onChange={() => {
                          const currentCount = formData.beverages[beverage] || 0;
                          handleNestedInputChange('beverages', beverage, currentCount > 0 ? 0 : 1);
                        }}
                        className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {beverage} - R{price} each
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Decor Package */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🎨</div>
                <h3 className="text-lg font-semibold text-gray-800">Decor Package</h3>
              </div>
              <select
                value={formData.decorPackage}
                onChange={(e) => handleInputChange('decorPackage', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              >
                {Object.entries(pricing.decor).map(([decorPackage, price]) => (
                  <option key={decorPackage} value={decorPackage}>
                    {decorPackage} - R{price}
                  </option>
                ))}
              </select>
            </div>

            {/* Spit Braai Rental */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🔥</div>
                <h3 className="text-lg font-semibold text-gray-800">Spit Braai Rental</h3>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleInputChange('spitBraaiRental', true)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    formData.spitBraaiRental
                      ? 'bg-gold text-white shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Yes (R1500)
                </button>
                <button
                  onClick={() => handleInputChange('spitBraaiRental', false)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    !formData.spitBraaiRental
                      ? 'bg-gold text-white shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Team Building Add-ons */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🤝</div>
                <h3 className="text-lg font-semibold text-gray-800">Team Building Add-ons</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(pricing.teamBuilding).map(([activity, price]) => (
                  <label key={activity} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.teamBuildingAddons.includes(activity)}
                      onChange={() => handleArrayToggle('teamBuildingAddons', activity)}
                      className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                    />
                    <span className="text-gray-700">
                      {activity} - R{price}/guest
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sound Hire */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🎧</div>
                <h3 className="text-lg font-semibold text-gray-800">Sound Hire</h3>
              </div>

              <select
                value={formData.soundHirePackageId}
                onChange={(e) =>
                  handleInputChange('soundHirePackageId', e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              >
                <option value="none">None</option>
                {soundHirePackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - R{pkg.price.toLocaleString()}/day
                  </option>
                ))}
              </select>

              <div className="mt-6">
                <h4 className="text-md font-semibold text-gray-800 mb-3">
                  Sound Add-ons
                </h4>
                <div className="space-y-3">
                  {soundAddOns.map((addOn) => (
                    <label
                      key={addOn.id}
                      className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.soundAddOnIds.includes(addOn.id)}
                        onChange={() => handleArrayToggle('soundAddOnIds', addOn.id)}
                        className="w-4 h-4 text-gold focus:ring-gold border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {addOn.name} - {addOn.priceLabel || `R${addOn.price.toLocaleString()}`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Smoke Machine Hire */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">💨</div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Smoke Machine Hire
                </h3>
              </div>

              <select
                value={formData.smokeMachinePackageId}
                onChange={(e) =>
                  handleInputChange('smokeMachinePackageId', e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              >
                <option value="none">None</option>
                {smokeMachinePackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - R{pkg.price.toLocaleString()}/day
                  </option>
                ))}
              </select>

              <p className="text-sm text-gray-600 mt-3 italic">
                Smoke effects are subject to venue rules and ventilation
                requirements.
              </p>
            </div>

            {/* Venue Support */}
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">🏟️</div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Venue Support
                </h3>
              </div>

              <select
                value={formData.venueSupportPackageId}
                onChange={(e) =>
                  handleInputChange('venueSupportPackageId', e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
              >
                <option value="none">None</option>
                {venueSupportPackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - R{pkg.price.toLocaleString()}
                  </option>
                ))}
              </select>

              {formData.venueSupportPackageId !== 'none' && (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred location
                    </label>
                    <input
                      type="text"
                      value={formData.venuePreferences.preferredLocation}
                      onChange={(e) =>
                        handleNestedInputChange(
                          'venuePreferences',
                          'preferredLocation',
                          e.target.value
                        )
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="e.g. Pretoria West"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Indoor / Outdoor
                      </label>
                      <select
                        value={formData.venuePreferences.indoorOutdoor}
                        onChange={(e) =>
                          handleNestedInputChange(
                            'venuePreferences',
                            'indoorOutdoor',
                            e.target.value
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="">Select</option>
                        <option value="indoor">Indoor</option>
                        <option value="outdoor">Outdoor</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Guest count
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.venuePreferences.guestCount}
                        onChange={(e) =>
                          handleNestedInputChange(
                            'venuePreferences',
                            'guestCount',
                            e.target.value
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="e.g. 80"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred event date
                      </label>
                      <input
                        type="date"
                        value={formData.venuePreferences.preferredEventDate}
                        onChange={(e) =>
                          handleNestedInputChange(
                            'venuePreferences',
                            'preferredEventDate',
                            e.target.value
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max venue budget
                      </label>
                      <input
                        type="text"
                        value={formData.venuePreferences.maxVenueBudget}
                        onChange={(e) =>
                          handleNestedInputChange(
                            'venuePreferences',
                            'maxVenueBudget',
                            e.target.value
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="e.g. R20,000"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tent, Chair & Table Hire */}
            <div className="card bg-gradient-to-br from-cream to-white border border-gold/20">
              <div className="flex items-center mb-4">
                <div className="w-5 h-5 text-gold mr-3 text-lg">⛺</div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Tent, Chair & Table Hire
                </h3>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tents
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.hireItems.tents}
                    onChange={(e) =>
                      handleNestedInputChange(
                        'hireItems',
                        'tents',
                        Math.max(0, parseInt(e.target.value, 10) || 0)
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chairs
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.hireItems.chairs}
                    onChange={(e) =>
                      handleNestedInputChange(
                        'hireItems',
                        'chairs',
                        Math.max(0, parseInt(e.target.value, 10) || 0)
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tables
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.hireItems.tables}
                    onChange={(e) =>
                      handleNestedInputChange(
                        'hireItems',
                        'tables',
                        Math.max(0, parseInt(e.target.value, 10) || 0)
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                </div>
              </div>

              <p className="text-sm text-olive mt-4 bg-olive/10 border border-olive/20 rounded-lg px-4 py-3">
                Quotation for hire items will be confirmed based on quantity and
                event requirements.
              </p>

              <button
                onClick={handleHireQuoteRequest}
                className="btn-secondary w-full py-3 mt-4 text-sm"
              >
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Request Hire Quote
              </button>
            </div>
            </div>
          </motion.div>

          {/* Estimate Display */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Total Estimate Card */}
            <div className="hidden lg:block card bg-gradient-to-br from-gold/10 to-olive/10 border-2 border-gold/20">
              <div className="text-center">
                <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-4">
                  Your Estimated Total
                </h3>
                <div className="text-5xl font-bold text-gold mb-2">
                  R{totalEstimate.toLocaleString()}
                </div>
                <div className="text-lg text-gray-600 mb-6">
                  R{Math.round(totalEstimate / formData.guests).toLocaleString()} per person
                </div>
                <p className="text-gray-600 mb-6">
                  This is an estimate based on your selections. Final pricing may vary based on specific requirements.
                </p>
                
                <button
                  onClick={handleWhatsAppSubmit}
                  className="btn-primary text-lg px-8 py-4 w-full mb-4"
                >
                  <MessageCircle className="w-5 h-5 inline mr-2" />
                  Send My Quote
                </button>
              </div>
            </div>

            {/* Breakdown */}
            <div className="card">
              <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">
                Live Summary Panel
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Selected services</span>
                  <span className="font-semibold text-right text-sm">
                    {[
                      'Catering',
                      formData.decorPackage ? 'Decor' : '',
                      formData.spitBraaiRental ? 'Spit Braai' : '',
                      (formData.soundHirePackageId !== 'none' || formData.soundAddOnIds.length > 0 || formData.smokeMachinePackageId !== 'none') ? 'Sound' : '',
                      formData.venueSupportPackageId !== 'none' ? 'Venue' : '',
                      (formData.teamBuildingAddons.length > 0 || formData.hireItems.tents > 0 || formData.hireItems.chairs > 0 || formData.hireItems.tables > 0) ? 'Extras' : '',
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between">
                  <span className="text-gray-600">Meal ({formData.mealType})</span>
                  <span className="font-semibold">R{(pricing.mealTypes[formData.mealType] * formData.guests).toLocaleString()}</span>
                </div>
                
                {formData.saladOptions.count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Salad ({formData.saladOptions.type})</span>
                    <span className="font-semibold">R{(pricing.salads[formData.saladOptions.type] * formData.saladOptions.count).toLocaleString()}</span>
                  </div>
                )}
                
                {formData.veggieOptions.count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Veggies ({formData.veggieOptions.type})</span>
                    <span className="font-semibold">R{(pricing.veggies[formData.veggieOptions.type] * formData.veggieOptions.count).toLocaleString()}</span>
                  </div>
                )}
                
                {formData.dessertOptions.count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dessert ({formData.dessertOptions.type})</span>
                    <span className="font-semibold">R{(pricing.desserts[formData.dessertOptions.type] * formData.dessertOptions.count).toLocaleString()}</span>
                  </div>
                )}
                
                {Object.entries(formData.extraMeatItems).map(([item, count]) => (
                  count > 0 && (
                    <div key={item} className="flex justify-between">
                      <span className="text-gray-600">{item} ({count}x)</span>
                      <span className="font-semibold">R{(pricing.extraMeatItems[item] * count).toLocaleString()}</span>
                    </div>
                  )
                ))}
                
                {Object.entries(formData.extraItems).map(([item, count]) => (
                  count > 0 && (
                    <div key={item} className="flex justify-between">
                      <span className="text-gray-600">{item} ({count}x)</span>
                      <span className="font-semibold">R{(pricing.extraItems[item] * count).toLocaleString()}</span>
                    </div>
                  )
                ))}
                
                {Object.entries(formData.staffOptions).map(([staff, count]) => (
                  count > 0 && (
                    <div key={staff} className="flex justify-between">
                      <span className="text-gray-600">{staff} ({count}x)</span>
                      <span className="font-semibold">R{(pricing.staffOptions[staff] * count).toLocaleString()}</span>
                    </div>
                  )
                ))}
                
                {Object.entries(formData.beverages).map(([beverage, count]) => (
                  count > 0 && (
                    <div key={beverage} className="flex justify-between">
                      <span className="text-gray-600">{beverage} ({count}x)</span>
                      <span className="font-semibold">R{(pricing.beverages[beverage] * count).toLocaleString()}</span>
                    </div>
                  )
                ))}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Decor ({formData.decorPackage})</span>
                  <span className="font-semibold">R{pricing.decor[formData.decorPackage].toLocaleString()}</span>
                </div>
                
                {formData.spitBraaiRental && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Spit Braai Rental</span>
                    <span className="font-semibold">R{pricing.spitBraai.toLocaleString()}</span>
                  </div>
                )}
                
                {formData.teamBuildingAddons.map(activity => (
                  <div key={activity} className="flex justify-between">
                    <span className="text-gray-600">{activity}</span>
                    <span className="font-semibold">R{(pricing.teamBuilding[activity] * formData.guests).toLocaleString()}</span>
                  </div>
                ))}

                {formData.soundHirePackageId !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Sound Hire ({soundHirePackages.find((p) => p.id === formData.soundHirePackageId)?.name || 'Package'})
                    </span>
                    <span className="font-semibold">
                      R{(pricing.soundHirePackages[formData.soundHirePackageId] || 0).toLocaleString()}
                    </span>
                  </div>
                )}

                {formData.soundAddOnIds.map((addOnId) => {
                  const addOn = soundAddOns.find((a) => a.id === addOnId);
                  if (!addOn) return null;
                  return (
                    <div key={addOnId} className="flex justify-between">
                      <span className="text-gray-600">{addOn.name}</span>
                      <span className="font-semibold">R{addOn.price.toLocaleString()}</span>
                    </div>
                  );
                })}

                {formData.smokeMachinePackageId !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Smoke Machine ({smokeMachinePackages.find((p) => p.id === formData.smokeMachinePackageId)?.name || 'Package'})
                    </span>
                    <span className="font-semibold">
                      R{(pricing.smokeMachinePackages[formData.smokeMachinePackageId] || 0).toLocaleString()}
                    </span>
                  </div>
                )}

                {formData.venueSupportPackageId !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Venue Support ({venueSupportPackages.find((p) => p.id === formData.venueSupportPackageId)?.name || 'Package'})
                    </span>
                    <span className="font-semibold">
                      R{(pricing.venueSupportPackages[formData.venueSupportPackageId] || 0).toLocaleString()}
                    </span>
                  </div>
                )}
                
                <hr className="my-3" />
                <div className="flex justify-between text-lg font-bold text-gold">
                  <span>Subtotal (Excl. VAT)</span>
                  <span>R{totalEstimate.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gold">
                  <span>Price per head (Excl. VAT)</span>
                  <span>R{Math.round(totalEstimate / formData.guests).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gold">
                  <span>VAT (15%)</span>
                  <span>R{Math.round(totalEstimate * 0.15).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-olive">
                  <span>Total (Incl. VAT)</span>
                  <span>R{Math.round(totalEstimate * 1.15).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-olive">
                  <span>Price per head (Incl. VAT)</span>
                  <span>R{Math.round((totalEstimate * 1.15) / formData.guests).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="card bg-blue-50 border border-blue-200">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">Important Note</h4>
                  <p className="text-sm text-blue-700">
                    This is a preliminary estimate. Final pricing may vary based on specific requirements, 
                    location accessibility, seasonal factors, and additional services. We'll provide a 
                    detailed quote after discussing your event needs.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        {/* Sticky mobile summary */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-gray-500">Estimated Total</div>
              <div className="text-lg font-bold text-gold truncate">
                R{totalEstimate.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 truncate">
                {formData.guests} guests • R
                {Math.round(totalEstimate / formData.guests).toLocaleString()} per person
              </div>
            </div>

            <button
              onClick={handleWhatsAppSubmit}
              className="btn-primary text-sm px-4 py-3 flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <MessageCircle size={16} />
              <span>Send</span>
            </button>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default EstimateCalculator;
